/**
 * ══════════════════════════════════════════════════════════════════════════
 * createPowerSyncQuery — Bridge PowerSync ↔ TanStack Solid Query
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Solid equivalent of the `usePowerSyncQuery` hooks that live in
 * `@fieldcheck/powersync` (React-only). Uses `db.onChangeWithCallback()` so the
 * query invalidates automatically when any dependent table changes,
 * integrating with TanStack Solid Query to share cache with mutations
 * (API/oRPC) that already live in `@fieldcheck/shared`.
 *
 * Reference:
 *   - apps/native/.../power-sync-provider.tsx
 *   - packages/powersync/src/query.ts (React version)
 *   - SOLID_MIGRATION_ROADMAP.md, Phase 4
 */

import type { AbstractPowerSyncDatabase, CompilableQuery, SyncStatus } from "@powersync/common";
import { toCompilableQuery } from "@powersync/drizzle-driver";
import type { Query } from "drizzle-orm";
import {
  createQuery,
  useQueryClient as getSolidQueryClient,
  type QueryKey,
} from "@tanstack/solid-query";
import { type Accessor, createEffect, createSignal, onCleanup } from "solid-js";
import { aggregateStreamProgress } from "../powersync/sync-progress";
import { createLatestThrottle } from "../powersync/status-throttle";
import { createInvalidationThrottle } from "./invalidation-throttle";
import { isDev } from "@fleet/utils";

// ═══════════════════════════════════════════════════════════════════════════
// Runtime helpers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * True when the JS is running inside a Tauri WebView. We branch on this in a
 * few places because the alpha `@powersync/tauri-plugin` SDK never sets
 * `currentStatus.hasSynced = true` — relying on it as a "wake up" signal for
 * watched queries causes them to stay frozen in Silent Sync forever.
 */
function isTauriRuntime(): boolean {
  if (typeof globalThis === "undefined") {
    return false;
  }
  const win = (globalThis as { window?: unknown }).window;
  if (typeof win !== "object" || win === null) {
    return false;
  }
  return "__TAURI_INTERNALS__" in win;
}

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export type PowerSyncTableRecord = object;

/**
 * Minimal reactive query result interface shared by both code paths in
 * `createPowerSyncQuery`:
 *   - the signal-based path (`suspense === false`), and
 *   - the standard TanStack `createQuery` path.
 *
 * `CreateQueryResult<T[], Error>` from TanStack is a structural superset of
 * this interface, so both branches satisfy it without casts.
 */
export interface CreatePowerSyncQueryResult<T extends PowerSyncTableRecord> {
  readonly data: T[] | undefined;
  readonly isFetching: boolean;
  readonly isLoading: boolean;
  readonly isPending: boolean;
  readonly error: Error | null;
  readonly isError: boolean;
  readonly status: "error" | "pending" | "success";
  readonly isPlaceholderData?: boolean;
  refetch(): void;
}

export type PowerSyncQueryFn<T extends PowerSyncTableRecord> = () =>
  | CompilableQuery<T>
  | { execute(): Promise<T | T[]>; toSQL(): Query }
  | null;

export interface CreatePowerSyncQueryArgs<T extends PowerSyncTableRecord> {
  /** Unique TanStack Query key. Reused for cross-client invalidations. */
  queryKey: Accessor<QueryKey> | QueryKey;
  /** Function that returns the compilable query (Drizzle or raw SQL). */
  queryFn: PowerSyncQueryFn<T>;
  /** PowerSync instance (usually comes from the provider). */
  db: Accessor<AbstractPowerSyncDatabase | null>;
  /** Optional: selectively disable. */
  enabled?: Accessor<boolean> | boolean;
  /** Optional: placeholder data (e.g. keepPreviousData). */
  placeholderData?: T[] | ((prev: T[] | undefined) => T[] | undefined);
  /** Optional: time in ms to consider the data fresh. */
  staleTime?: number;
  /** Optional: time in ms to keep data cached after disuse. */
  gcTime?: number;
  /** Optional: window in ms of the invalidation throttle. Defaults to `DEFAULT_THROTTLE_MS`.*/
  throttleTime?: number;
  /**
   * Optional: whether the query should integrate with Solid Suspense.
   *
   * Defaults to `false` (non-suspending). This is a local-first app: SQLite
   * reads resolve in ~ms, so suspending the tree to show a fallback does MORE
   * harm (flash + remount) than rendering the content directly with
   * `keepPreviousData`. Pass `suspense: true` only where you really want to
   * block behind a `<Suspense>` while loading.
   */
  suspense?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════════════════

// Window of the invalidation throttle: the first table change refetches at
// once, later changes inside the window merge into one refetch when it closes
// (see `invalidation-throttle.ts`). It never delays a lone change.
export const DEFAULT_THROTTLE_MS = 150;
// A query waits for the next macrotask, not for a fixed delay, before it runs:
// reactive changes made in the same task still collapse into one execution
// (the earlier ones are aborted), but a mount or a refetch is not held back.
const QUERY_YIELD_MS = 0;

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

function isAccessor<V>(value: Accessor<V> | V): value is Accessor<V> {
  return typeof value === "function";
}

function resolveAccessor<V>(value: Accessor<V> | V): V {
  return isAccessor(value) ? value() : value;
}

// The set of tables a query reads depends only on its SQL *shape*, never on the
// bound parameter values (`... WHERE list_id = ?` always touches the same
// tables). PowerSync's `resolveTables` resolves it with an `EXPLAIN` + a
// `sqlite_master` lookup on every (re)subscribe — so switching lists re-runs
// that exact pair of queries for SQL it has already resolved. Cache the result
// by SQL string (stable for the app session) so a list switch reuses it instead
// of hitting SQLite again.
// We cache the in-flight *promise* (not the resolved value) so that several
// queries sharing the same SQL — e.g. the main header and sticky header both
// running the same `count(...)` on a list switch — dedupe onto a single
// `resolveTables` call instead of each firing its own EXPLAIN before the first
// completes. A rejected lookup is evicted so it can be retried.
const resolvedTablesCache = new Map<string, Promise<string[]>>();

// Intentionally NOT async: it must synchronously store the in-flight promise in
// the cache before any await so concurrent callers dedupe onto it — an async
// function can't reference its own returned promise to cache it.
// oxlint-disable-next-line typescript/promise-function-async
function resolveTablesCached(
  db: AbstractPowerSyncDatabase,
  sql: string,
  params: readonly unknown[] | undefined,
): Promise<string[]> {
  const cached = resolvedTablesCache.get(sql);
  if (cached) {
    return cached;
  }
  // Cache the promise synchronously (before awaiting) so concurrent callers
  // share it; evict on failure so a transient error can be retried.
  const pending = (async (): Promise<string[]> => {
    try {
      return await db.resolveTables(sql, params ? [...params] : undefined);
    } catch (err) {
      resolvedTablesCache.delete(sql);
      throw err;
    }
  })();
  resolvedTablesCache.set(sql, pending);
  return pending;
}

// ═══════════════════════════════════════════════════════════════════════════
// Public API
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Creates a reactive query over PowerSync integrated with TanStack Solid Query.
 *
 * Strategy:
 *   1. TanStack's `createQuery` manages cache, error, loading.
 *   2. `queryFn` runs the `CompilableQuery` with `db.execute()`.
 *   3. `db.onChangeWithCallback` invalidates the query key as soon as any
 *      referenced table changes, which triggers a refetch.
 *
 * @example
 * ```tsx
 * const inspections = createPowerSyncQuery({
 *   queryKey: () => ["inspections", userId()],
 *   queryFn: () =>
 *     drizzleDb.select().from(inspection).where(eq(inspection.user_id, userId())),
 *   db: () => powerSyncCtx.db,
 *   enabled: () => Boolean(userId()),
 * });
 * ```
 */
export function createPowerSyncQuery<T extends PowerSyncTableRecord>(
  args: CreatePowerSyncQueryArgs<T>,
): CreatePowerSyncQueryResult<T> {
  function getEnabled(): boolean {
    if (!args.db()) {
      return false;
    }
    return resolveAccessor(args.enabled ?? true);
  }

  // ── Non-suspending signal-based path (DEFAULT) ─────────────────────────────
  // Local-first default: only opt INTO Solid Suspense with an explicit
  // `suspense: true`. Everything else takes the signal path so a sub-100ms
  // local read never blanks/flashes the tree behind a <Suspense> boundary.
  if (args.suspense !== true) {
    const [data, setData] = createSignal<T[]>([]);
    const [isFetching, setIsFetching] = createSignal(false);
    const [error, setError] = createSignal<Error | null>(null);
    const [trigger, setTrigger] = createSignal(0);

    // Mirror fetched rows into the TanStack cache under this query's key. The
    // suspending path registers a real `createQuery` (which populates the
    // cache); the signal path does not, so cache-only readers — e.g. the
    // dashboard's keyed `createQuery` observers with no queryFn — would
    // otherwise find no data and throw "Missing queryFn". Writing here keeps
    // those read-only mirrors working without suspending the shell.
    const signalQueryClient = getSolidQueryClient();

    // 1. Table change subscription
    createEffect(() => {
      const db = args.db();
      if (!db || !getEnabled()) {
        return;
      }

      const queryInput = args.queryFn();
      if (!queryInput) {
        return;
      }
      const query =
        "toSQL" in queryInput
          ? toCompilableQuery(queryInput as { execute(): Promise<T | T[]>; toSQL(): Query })
          : queryInput;

      const compiled = query.compile();
      const params = compiled.parameters as readonly unknown[] | undefined;
      const abortController = new AbortController();
      let cancelled = false;
      const cleanups: (() => void)[] = [];

      const invalidation = createInvalidationThrottle(() => {
        setTrigger((t) => t + 1);
      }, args.throttleTime ?? DEFAULT_THROTTLE_MS);

      // NOTE (CC-15): this runs only from `onChange`, which fires for ACTUAL
      // table changes after we've subscribed (local writes, or sync ops applied
      // to a watched table). The Silent-Sync main-thread protection lives at
      // subscribe time (`subscribeToTables` defers until `hasSynced` when
      // connected). We must NOT additionally drop invalidations here while
      // `!hasSynced`: a user can subscribe while OFFLINE (so the deferral never
      // triggered), then the connection comes up mid-session with
      // `connected && !hasSynced`. In that window a *local* write — e.g.
      // creating a tag in the composer — fires onChange, and dropping it left
      // the query stale until a full reload. The throttle below already caps the
      // refetch rate, so honour every change.
      const requestInvalidation = () => {
        invalidation.request();
      };

      async function subscribeToTables(currentDb: AbstractPowerSyncDatabase): Promise<void> {
        try {
          const tables = await resolveTablesCached(currentDb, compiled.sql, params);
          if (cancelled) {
            return;
          }

          const status = currentDb.currentStatus;
          if (!isTauriRuntime() && !status.hasSynced && status.connected) {
            const unregisterStatus = currentDb.registerListener({
              statusChanged: (s) => {
                if (s.hasSynced) {
                  setTrigger((t) => t + 1);
                  unregisterStatus();
                  void subscribeToTables(currentDb);
                }
              },
            });
            cleanups.push(() => {
              unregisterStatus();
            });
            return;
          }

          // See the equivalent comment in the TanStack path: the first onChange
          // from `triggerImmediate` is redundant with the data-fetching effect's
          // own initial run. Skip it to avoid a double fetch on mount.
          let isFirstChange = true;

          currentDb.onChangeWithCallback(
            {
              onChange: () => {
                if (isFirstChange) {
                  isFirstChange = false;
                  return;
                }
                requestInvalidation();
              },
              onError: (err: unknown) => {
                console.warn("[createPowerSyncQuery] watch error:", err);
              },
            },
            // `triggerImmediate` makes PowerSync emit the query's initial state
            // right after subscribing — the canonical way to load data without a
            // global invalidate. It also closes the startup race where data lands
            // during the async `resolveTables` gap. Identical on every platform.
            { tables, signal: abortController.signal, triggerImmediate: true },
          );
        } catch (err) {
          console.warn("[createPowerSyncQuery] resolveTables error:", err);
        }
      }

      void subscribeToTables(db);

      onCleanup(() => {
        cancelled = true;
        abortController.abort();
        invalidation.cancel();
        for (const cleanup of cleanups) {
          cleanup();
        }
      });
    });

    // 2. Data fetching execution
    createEffect(() => {
      trigger(); // track trigger for manual/sub re-fetches
      const db = args.db();
      const enabled = getEnabled();
      if (!db || !enabled) {
        setData([]);
        setIsFetching(false);
        setError(null);
        return;
      }

      const queryInput = args.queryFn();
      if (!queryInput) {
        setData([]);
        setIsFetching(false);
        setError(null);
        return;
      }

      const query =
        "toSQL" in queryInput
          ? toCompilableQuery(queryInput as { execute(): Promise<T | T[]>; toSQL(): Query })
          : queryInput;

      setIsFetching(true);
      setError(null);

      const fetchController = new AbortController();
      let delayTimeout: ReturnType<typeof setTimeout> | null = null;

      delayTimeout = setTimeout(() => {
        delayTimeout = null;
        void (async () => {
          try {
            const compiled = query.compile();
            const start = isDev ? performance.now() : 0;

            const result = await query.execute();

            if (isDev) {
              const duration = performance.now() - start;
              console.log(
                `[Query] ${compiled.sql} | Params: ${JSON.stringify(compiled.parameters)} | Duration: ${duration.toFixed(2)}ms`,
              );
            }

            if (fetchController.signal.aborted) {
              return;
            }
            const arrayResult = Array.isArray(result) ? result : [result];
            setData(arrayResult);
            signalQueryClient.setQueryData(resolveAccessor(args.queryKey), arrayResult);
            setIsFetching(false);
          } catch (err) {
            if (fetchController.signal.aborted) {
              return;
            }
            setError(err instanceof Error ? err : new Error(String(err)));
            setIsFetching(false);
          }
        })();
      }, QUERY_YIELD_MS);

      onCleanup(() => {
        fetchController.abort();
        if (delayTimeout) {
          clearTimeout(delayTimeout);
        }
      });
    });

    const result: CreatePowerSyncQueryResult<T> = {
      get data(): T[] | undefined {
        return data();
      },
      get isFetching(): boolean {
        return isFetching();
      },
      get isLoading(): boolean {
        return isFetching() && data().length === 0;
      },
      get isPending(): boolean {
        return isFetching() && data().length === 0;
      },
      get error(): Error | null {
        return error();
      },
      get isError(): boolean {
        return error() !== null;
      },
      get status(): "error" | "pending" | "success" {
        if (error()) {
          return "error";
        }
        if (isFetching()) {
          return "pending";
        }
        return "success";
      },
      refetch: () => {
        setTrigger((t) => t + 1);
      },
    };
    return result;
  }

  // ── Standard TanStack Query path ───────────────────────────────────────────
  const queryClient = getSolidQueryClient();

  function getQueryKey(): QueryKey {
    return resolveAccessor(args.queryKey);
  }

  // 1. Table change subscription — invalidates TanStack when changes are detected.
  createEffect(() => {
    const db = args.db();
    if (!db) {
      return;
    }
    if (!getEnabled()) {
      return;
    }

    const queryInput = args.queryFn();
    if (!queryInput) {
      return;
    }
    const query =
      "toSQL" in queryInput
        ? toCompilableQuery(queryInput as { execute(): Promise<T | T[]>; toSQL(): Query })
        : queryInput;

    const compiled = query.compile();
    const params = compiled.parameters as readonly unknown[] | undefined;
    const abortController = new AbortController();
    let cancelled = false;
    const cleanups: (() => void)[] = [];

    // Throttle helper to avoid bursts of invalidations
    const invalidation = createInvalidationThrottle(() => {
      void queryClient.invalidateQueries({ queryKey: getQueryKey() });
    }, args.throttleTime ?? DEFAULT_THROTTLE_MS);

    // See the longer note on the signal-path `requestInvalidation` (CC-15): the
    // Silent-Sync main-thread protection belongs at subscribe time
    // (`subscribeToTables` defers until `hasSynced` when connected), NOT here.
    // `onChange` only fires for actual changes on a watched table once we've
    // subscribed. Dropping invalidations while `connected && !hasSynced` lost
    // LOCAL writes made in the window after subscribing offline and then
    // reconnecting mid-session (e.g. creating a tag in the composer), leaving
    // the query stale until a full reload. The throttle already caps the rate.
    const requestInvalidation = () => {
      invalidation.request();
    };

    async function subscribeToTables(currentDb: AbstractPowerSyncDatabase): Promise<void> {
      try {
        const tables = await resolveTablesCached(currentDb, compiled.sql, params);
        if (cancelled) {
          return;
        }

        // Silent Sync: If we are in the initial sync phase, don't subscribe to
        // individual table changes yet. This prevents the main thread from
        // being overwhelmed by thousands of table change events.
        //
        // Skipped on Tauri: the alpha @powersync/tauri-plugin SDK never
        // surfaces `hasSynced = true` (the sync loop runs in Rust off the
        // main thread, and the bridged status event for `hasSynced` is not
        // implemented). If we kept Silent Sync gated on `hasSynced`, every
        // query would stay subscribed only to status events forever and
        // local writes would never refresh the UI. Tauri doesn't need the
        // optimization anyway — table-change events come pre-batched from
        // the Rust side, no risk of swamping the JS thread.
        const status = currentDb.currentStatus;
        if (!isTauriRuntime() && !status.hasSynced && status.connected) {
          console.log(`[PowerSync] Silent Sync active for query: ${JSON.stringify(getQueryKey())}`);

          // We subscribe to the STATUS change instead, to know when to "wake up"
          const unregisterStatus = currentDb.registerListener({
            statusChanged: (s) => {
              if (s.hasSynced) {
                console.log(
                  `[PowerSync] Initial sync complete. Waking up query: ${JSON.stringify(getQueryKey())}`,
                );
                void queryClient.invalidateQueries({ queryKey: getQueryKey() });
                unregisterStatus();
                void subscribeToTables(currentDb); // Re-subscribe normally
              }
            },
          });
          cleanups.push(() => {
            unregisterStatus();
          });
          return;
        }

        // Track whether the first onChange (from triggerImmediate) has fired.
        // `triggerImmediate` emits once on subscribe so PowerSync knows the
        // current table state — but TanStack Query's own mount already runs the
        // queryFn at that same moment.  Invalidating again creates a double
        // fetch on every component mount.  We skip only that very first call;
        // every subsequent onChange is a real DB change that must refetch.
        let isFirstChange = true;

        currentDb.onChangeWithCallback(
          {
            onChange: () => {
              if (isFirstChange) {
                isFirstChange = false;
                return;
              }
              requestInvalidation();
            },
            onError: (error: unknown) => {
              console.warn("[createPowerSyncQuery] watch error:", error);
            },
          },
          // `triggerImmediate`: emit the initial state on subscribe (loads data
          // without a global invalidate) and close the startup race where rows
          // land during the async `resolveTables` gap. Identical per platform.
          { tables, signal: abortController.signal, triggerImmediate: true },
        );
      } catch (error) {
        console.warn("[createPowerSyncQuery] resolveTables error:", error);
      }
    }

    void subscribeToTables(db);

    onCleanup(() => {
      cancelled = true;
      abortController.abort();
      invalidation.cancel();
      for (const cleanup of cleanups) {
        cleanup();
      }
    });
  });

  // 2. TanStack query that runs the actual SQL.
  return createQuery<T[]>(() => ({
    queryKey: getQueryKey(),
    enabled: getEnabled(),
    queryFn: async (context): Promise<T[]> => {
      const db = args.db();
      if (!db) {
        throw new Error("[createPowerSyncQuery] PowerSync database is not initialized");
      }
      const queryInput = args.queryFn();
      if (!queryInput) {
        return [];
      }
      const query =
        "toSQL" in queryInput
          ? toCompilableQuery(queryInput as { execute(): Promise<T | T[]>; toSQL(): Query })
          : queryInput;

      if (context.signal.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
      await new Promise<void>((resolve, reject) => {
        let timeout: ReturnType<typeof setTimeout> | undefined = undefined;
        function onAbort() {
          if (timeout) {
            clearTimeout(timeout);
          }
          reject(new DOMException("Aborted", "AbortError"));
        }
        timeout = setTimeout(() => {
          context.signal.removeEventListener("abort", onAbort);
          resolve();
        }, QUERY_YIELD_MS);
        context.signal.addEventListener("abort", onAbort);
      });

      const compiled = query.compile();
      const start = isDev ? performance.now() : 0;

      const result = await query.execute();

      if (isDev) {
        const duration = performance.now() - start;
        console.log(
          `[Query] ${compiled.sql} | Params: ${JSON.stringify(compiled.parameters)} | Duration: ${duration.toFixed(2)}ms`,
        );
      }

      return result;
    },
    placeholderData: args.placeholderData,
    staleTime: args.staleTime,
    gcTime: args.gcTime,
    suspense: args.suspense,
  }));
}

/**
 * Variant for queries that return a single record (limit 1).
 */
export function createPowerSyncSingle<T extends PowerSyncTableRecord>(
  args: CreatePowerSyncQueryArgs<T>,
) {
  const query = createPowerSyncQuery<T>(args);

  return {
    get data(): T | null {
      return query.data?.[0] ?? null;
    },
    get isLoading(): boolean {
      return query.isLoading;
    },
    get isFetching(): boolean {
      return query.isFetching;
    },
    get error(): Error | null {
      return query.error;
    },
  };
}

/**
 * Creates a reactive store that tracks PowerSync's sync status.
 */
export function createPowerSyncStatus(db: Accessor<AbstractPowerSyncDatabase | null>) {
  const [status, setStatus] = createSignal<AbstractPowerSyncDatabase["currentStatus"] | null>(null);

  createEffect(() => {
    const currentDb = db();
    if (!currentDb) {
      setStatus(null);
      return;
    }

    setStatus(currentDb.currentStatus);
    // Latest-value throttle: the final status of a burst (sync finished) must
    // never be dropped — see `status-throttle.ts`.
    const throttle = createLatestThrottle<SyncStatus>((s) => {
      setStatus(s);
    }, DEFAULT_THROTTLE_MS);

    const unregister = currentDb.registerListener({
      statusChanged: (s) => {
        throttle.push(s);
      },
    });

    onCleanup(() => {
      unregister();
      throttle.cancel();
    });
  });

  return status;
}

export interface PowerSyncStatusSnapshot {
  hasSynced: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  isDownloading: boolean;
  isUploading: boolean;
  isOnline: boolean;
  downloadedFraction: number | null;
  downloadedOperations: number | null;
  totalOperations: number | null;
  /** Sync streams the client is tracking. 0 until the first status line lands.*/
  streamCount: number;
  /** How many of those have completed at least one sync.*/
  syncedStreamCount: number;
}

const EMPTY_STATUS: PowerSyncStatusSnapshot = {
  hasSynced: false,
  isConnected: false,
  isConnecting: false,
  isDownloading: false,
  isUploading: false,
  isOnline: typeof navigator === "undefined" ? true : navigator.onLine,
  downloadedFraction: null,
  downloadedOperations: null,
  totalOperations: null,
  streamCount: 0,
  syncedStreamCount: 0,
};

/**
 * Flattens a `SyncStatus` into the snapshot the UI reads.
 *
 * Progress and first-sync completion come from the PER-STREAM counters, never
 * from `status.downloadProgress` / `status.hasSynced`. Under Tauri those two are
 * a trap — `downloadProgress` is non-null but all-zero, and `hasSynced` is never
 * serialised at all. `sync-progress.ts` documents the SDK sources and the
 * captured payload that prove it.
 *
 * Deliberately NOT branched on `isTauriRuntime()`: the core extension feeds the
 * same `internalStreamSubscriptions` to the web SDK, so both platforms run this
 * exact code. That is what makes a green check on web say something about Tauri.
 */
function snapshotFromStatus(
  status: SyncStatus | undefined,
  isOnline: boolean,
): PowerSyncStatusSnapshot {
  if (!status) {
    return { ...EMPTY_STATUS, isOnline };
  }
  const dl = status.dataFlowStatus;
  const progress = aggregateStreamProgress(status.syncStreams);
  return {
    hasSynced: progress.hasSynced,
    isConnected: status.connected,
    isConnecting: status.connecting,
    isDownloading: dl.downloading ?? false,
    isUploading: dl.uploading ?? false,
    isOnline,
    downloadedFraction: progress.downloadedFraction,
    downloadedOperations: progress.downloadedOperations,
    totalOperations: progress.totalOperations,
    streamCount: progress.streamCount,
    syncedStreamCount: progress.syncedStreamCount,
  };
}

/**
 * The first-sync phase of a status: what the overlay's state machine branches
 * on (connected, downloading, first sync complete — the latter from the
 * per-stream counters, like everything else here). Statuses of different
 * phases must never be coalesced by the status throttle.
 */
function syncStatusPhase(status: SyncStatus): string {
  const { hasSynced } = aggregateStreamProgress(status.syncStreams);
  const downloading = status.dataFlowStatus.downloading ?? false;
  return `${String(status.connected)}|${String(downloading)}|${String(hasSynced)}`;
}

/**
 * Extended reactive hook with progress snapshot and online state.
 */
export function createExtendedPowerSyncStatus(db: Accessor<AbstractPowerSyncDatabase | null>) {
  const [status, setStatus] = createSignal<PowerSyncStatusSnapshot>(EMPTY_STATUS);

  // Browser online/offline
  createEffect(() => {
    const updateOnline = () => {
      const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;
      setStatus((prev) => ({ ...prev, isOnline }));
    };
    globalThis.addEventListener("online", updateOnline);
    globalThis.addEventListener("offline", updateOnline);
    updateOnline();
    onCleanup(() => {
      globalThis.removeEventListener("online", updateOnline);
      globalThis.removeEventListener("offline", updateOnline);
    });
  });

  // Subscribe to PowerSync status changes
  createEffect(() => {
    const currentDb = db();
    if (!currentDb) {
      setStatus((prev) => ({ ...EMPTY_STATUS, isOnline: prev.isOnline }));
      return;
    }
    const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;
    setStatus(snapshotFromStatus(currentDb.currentStatus, isOnline));

    // Latest-value throttle: publishing the FIRST status of each window and
    // dropping the rest lost the final "download finished" status, leaving the
    // first-sync pill stuck — see `status-throttle.ts`.
    //
    // Phase-aware: a small first sync fits connecting → downloading → synced
    // inside one window, and merging across those phases published only
    // `synced` — the overlay's machine never entered `downloading` and the
    // determinate bar never mounted (see `syncStatusPhase`).
    const throttle = createLatestThrottle<SyncStatus>(
      (s) => {
        const online = typeof navigator === "undefined" ? true : navigator.onLine;
        setStatus(snapshotFromStatus(s, online));
      },
      DEFAULT_THROTTLE_MS,
      { phaseOf: syncStatusPhase },
    );
    const unsubscribe = currentDb.registerListener({
      statusChanged: (s) => {
        throttle.push(s);
      },
    });
    onCleanup(() => {
      unsubscribe();
      throttle.cancel();
    });
  });

  return status;
}
